/**
 * useAutoSave — Handles auto-save timer and beforeunload warning.
 * Auto-saves every INTERVAL seconds if there are unsaved changes and a project is loaded.
 * Shows browser warning on close/navigate when unsaved changes exist.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useWorkflowStore } from '../store/workflowStore';
import { toast } from 'sonner';
import { recordAd2Metric } from '../utils/ad2Metrics';

const AUTO_SAVE_INTERVAL = 300000; // 5 minutes
export const AD2_AUTOSAVE_ENABLED = process.env.REACT_APP_AD2_LITE_AUTOSAVE !== 'false';
export const AD2_DEBOUNCE_MS = 3000;
export const AD2_MAX_WAIT_MS = 10000;

export const useAutoSave = () => {
  const hasUnsavedChanges = useWorkflowStore(s => s.hasUnsavedChanges);
  const currentProjectId = useWorkflowStore(s => s.currentProjectId);
  const projectName = useWorkflowStore(s => s.projectName);
  const nodes = useWorkflowStore(s => s.nodes);
  const edges = useWorkflowStore(s => s.edges);
  const projectDescription = useWorkflowStore(s => s.projectDescription);
  const nodeIdCounter = useWorkflowStore(s => s.nodeIdCounter);
  const debounceRef = useRef(null);
  const maxWaitRef = useRef(null);
  const retryRef = useRef(0);
  const mountedRef = useRef(true);

  // Auto-save logic
  const doAutoSave = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (maxWaitRef.current) clearTimeout(maxWaitRef.current);
    debounceRef.current = null;
    maxWaitRef.current = null;
    const fresh = useWorkflowStore.getState();
    if (!fresh.currentProjectId || !fresh.hasUnsavedChanges) return;
    if (fresh.isSavingProject) {
      debounceRef.current = setTimeout(doAutoSave, 500);
      return;
    }
    const started = performance.now();
    recordAd2Metric('autosave.request');
    try {
      await fresh.saveProject();
      retryRef.current = 0;
      recordAd2Metric('autosave.success');
      recordAd2Metric('autosave.latency_ms', performance.now() - started);
    } catch (err) {
      recordAd2Metric('autosave.failure');
      toast.error('Error al auto-guardar', { duration: 3000, position: 'bottom-right' });
      if (mountedRef.current && useWorkflowStore.getState().hasUnsavedChanges) {
        const delay = [2000, 5000, 10000][Math.min(retryRef.current, 2)];
        retryRef.current += 1;
        recordAd2Metric('autosave.retry');
        debounceRef.current = setTimeout(doAutoSave, delay);
      }
    }
  }, []);

  // AD2: debounce desde el último cambio y límite máximo desde el primero.
  useEffect(() => {
    if (!currentProjectId || !hasUnsavedChanges) return undefined;
    if (!AD2_AUTOSAVE_ENABLED) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(doAutoSave, AD2_DEBOUNCE_MS);
    if (!maxWaitRef.current) {
      maxWaitRef.current = setTimeout(() => {
        maxWaitRef.current = null;
        doAutoSave();
      }, AD2_MAX_WAIT_MS);
    }
    return undefined;
  }, [currentProjectId, hasUnsavedChanges, nodes, edges, projectName,
    projectDescription, nodeIdCounter, doAutoSave]);

  // Flag de rollback: restaura el intervalo fijo anterior sin depender de
  // cada movimiento del canvas.
  useEffect(() => {
    if (AD2_AUTOSAVE_ENABLED || !currentProjectId || !hasUnsavedChanges) return undefined;
    const legacy = setInterval(doAutoSave, AUTO_SAVE_INTERVAL);
    return () => clearInterval(legacy);
  }, [currentProjectId, hasUnsavedChanges, doAutoSave]);

  useEffect(() => () => {
    mountedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (maxWaitRef.current) clearTimeout(maxWaitRef.current);
  }, []);

  // beforeunload warning
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Tienes cambios sin guardar. ¿Seguro que deseas salir?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // E1 · SAVE_CONFLICT_GUARD — al volver a la pestaña chequeamos si alguien
  // (otra pestaña o el agente por MCP) guardó una versión más nueva.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        useWorkflowStore.getState().checkProjectVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);
};
